/**
 * fetchStrapiCollection — the single sanctioned way to read a Strapi REST collection.
 *
 * Strapi's REST API caps every collection response at `maxLimit: 100` rows and
 * defaults to 25 (`cms/server/config/api.ts`). A plain unpaginated `fetch`
 * therefore silently returns at most 25 rows — code that assumed it received the
 * whole collection silently lost data (see MWBI-725, and MWBI-563 before it).
 *
 * This helper pages through the ENTIRE collection and returns every matching
 * row, or throws. It never returns a partial set that looks complete.
 *
 * Some endpoints — notably users-permissions `/api/users` — ignore REST
 * pagination and return the whole collection on every request. The helper
 * detects that and returns in a single request instead of looping forever.
 *
 * Do not hand-roll pagination loops anywhere else. Use this.
 */
import qs from 'qs';

/**
 * Strapi `maxLimit` from cms/server/config/api.ts. The helper never *requests*
 * a larger page than this; a non-paginating endpoint may still *return* more
 * (which is exactly what the over-size guard below detects).
 */
const STRAPI_MAX_PAGE_SIZE = 100;

/** Default proxy base — all browser calls route through the BFF proxy. */
const DEFAULT_BASE_URL = '/api/strapi';

/** Page requests kept in flight at once when fetching pages 2..N of a known-size collection. */
const DEFAULT_CONCURRENCY = 5;

/**
 * Runaway guard. 1000 pages * 100 rows = 100k rows — far beyond any legitimate
 * collection in this app. Hitting this throws loudly instead of hanging.
 */
const DEFAULT_MAX_PAGES = 1000;

/**
 * AWS WAF's `SizeRestrictions_QUERYSTRING` rule (AWSManagedRulesCommonRuleSet,
 * enabled with no override in `terraform/waf.tf`) rejects any request whose
 * query string exceeds 2048 bytes. The ALB returns a bare 403 HTML page before
 * the request ever reaches Strapi, so the app sees a generic failure with no
 * hint about the cause.
 *
 * There is NO WAF in front of a local dev server, so an over-long query string
 * works locally and fails only once deployed — which is exactly how MWBI-934
 * shipped. Enforcing the same ceiling client-side makes local behaviour match
 * production and converts an opaque 403 into an actionable error naming the fix.
 */
const MAX_QUERY_STRING_BYTES = 2048;

/**
 * Ids per request in `fetchStrapiCollectionByIds`. Budget for the worst case
 * seen in this app — a 24-char Strapi documentId under a nested relation
 * filter, e.g. `filters[news][documentId][$in][12]=<24 chars>` — is ~60 bytes
 * per id, so 15 ids ≈ 900 bytes. That leaves over half of MAX_QUERY_STRING_BYTES
 * for `populate`/`fields`/`sort` (the heaviest populate in this app,
 * newsStore's commentPopulate, is ~330 bytes).
 */
const DEFAULT_ID_CHUNK_SIZE = 15;

/** Query parameters accepted by the helper. Pagination is intentionally NOT accepted — the helper owns it. */
export interface StrapiCollectionParams {
  filters?: Record<string, unknown>;
  populate?: unknown;
  fields?: readonly string[];
  /** Strapi sort spec. Defaults to `id:asc` for deterministic, stable paging. */
  sort?: string | readonly string[];
}

export interface FetchCollectionOptions {
  /** Abort in-flight requests. Forwarded to every page fetch. */
  signal?: AbortSignal;
  /** Rows per request. Clamped to [1, 100]. Default 100 (fewest round-trips). */
  pageSize?: number;
  /** Maximum pages before throwing (runaway guard). Default 1000. */
  maxPages?: number;
  /** Concurrent page requests for pages 2..N. Default 5. */
  concurrency?: number;
  /** Override the proxy base. Mainly for tests. Default `/api/strapi`. */
  baseUrl?: string;
}

/** Thrown when a page request fails, returns malformed data, or the collection exceeds `maxPages`. */
export class StrapiCollectionError extends Error {
  constructor(
    message: string,
    readonly path: string,
    readonly page?: number,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'StrapiCollectionError';
  }
}

/** Standard Strapi 5 collection response. `/users` (users-permissions) instead returns a bare array. */
interface StrapiPage<T> {
  data?: T[];
  meta?: { pagination?: { pageCount?: number } };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function pageRange(start: number, end: number): number[] {
  const pages: number[] = [];
  for (let page = start; page <= end; page += 1) pages.push(page);
  return pages;
}

/**
 * True when two fetched pages hold the same rows in the same order — the
 * signature of an endpoint that ignores the `page` parameter and re-serves the
 * whole collection (users-permissions `/api/users`). Rows are compared by `id`
 * (every Strapi entity carries a unique `id`); a row without one falls back to
 * referential identity.
 */
function isSamePage(a: readonly unknown[], b: readonly unknown[]): boolean {
  if (a.length === 0 || a.length !== b.length) return false;
  const idOf = (row: unknown): unknown =>
    row !== null && typeof row === 'object' && 'id' in row
      ? (row as { id: unknown }).id
      : row;
  return a.every((row, index) => idOf(row) === idOf(b[index]));
}

/** Run `task` over `items` with at most `limit` in flight; preserves input order in the result. */
async function mapWithConcurrency<I, O>(
  items: readonly I[],
  limit: number,
  task: (item: I) => Promise<O>,
): Promise<O[]> {
  const results = new Array<O>(items.length);
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await task(items[index] as I);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

/**
 * Fetch every row of a Strapi collection, paging transparently.
 *
 * @param path   Collection path, e.g. `group-members` (leading slash optional).
 * @param params filters / populate / fields / sort. Pagination is owned by the helper.
 * @param options pageSize, concurrency, maxPages, signal, baseUrl.
 * @returns every matching row, in order.
 * @throws {StrapiCollectionError} on any page failure — never a silent partial result.
 */
export async function fetchStrapiCollection<T = unknown>(
  path: string,
  params: StrapiCollectionParams = {},
  options: FetchCollectionOptions = {},
): Promise<T[]> {
  const baseUrl = options.baseUrl ?? DEFAULT_BASE_URL;
  const pageSize = clamp(Math.trunc(options.pageSize ?? STRAPI_MAX_PAGE_SIZE), 1, STRAPI_MAX_PAGE_SIZE);
  const maxPages = Math.max(1, Math.trunc(options.maxPages ?? DEFAULT_MAX_PAGES));
  const concurrency = Math.max(1, Math.trunc(options.concurrency ?? DEFAULT_CONCURRENCY));
  const normalizedPath = path.replace(/^\/+/, '');

  // Deterministic sort is mandatory: without it Strapi returns rows in unspecified
  // DB order, so *which* rows land outside a page boundary is non-deterministic —
  // that is what made MWBI-725 intermittent. `id:asc` is always appended as a
  // final tiebreaker: a caller's non-unique sort key (createdAt, date, order…)
  // could otherwise let a tied row drift across a page boundary during paging.
  const callerSort = params.sort ?? [];
  const sortFields = Array.isArray(callerSort) ? [...callerSort] : [callerSort];
  if (!sortFields.some((field) => field === 'id' || field.startsWith('id:'))) {
    sortFields.push('id:asc');
  }

  const buildUrl = (page: number): string => {
    const query = qs.stringify(
      {
        filters: params.filters,
        populate: params.populate,
        fields: params.fields,
        sort: sortFields,
        pagination: { page, pageSize },
      },
      { encodeValuesOnly: true },
    );

    // Fail loudly and locally rather than eating an opaque WAF 403 in a
    // deployed environment (MWBI-934). Measured in bytes, not characters,
    // because that is what the WAF rule counts.
    const queryBytes = new TextEncoder().encode(query).length;
    if (queryBytes > MAX_QUERY_STRING_BYTES) {
      throw new StrapiCollectionError(
        `Query string for "${normalizedPath}" is ${queryBytes} bytes, over the ${MAX_QUERY_STRING_BYTES}-byte limit AWS WAF enforces. ` +
          'Split the request — use fetchStrapiCollectionByIds() for an $in filter, or narrow populate/fields.',
        normalizedPath,
        page,
      );
    }

    return `${baseUrl}/${normalizedPath}?${query}`;
  };

  const fetchPage = async (page: number): Promise<{ rows: T[]; pageCount?: number }> => {
    let response: { ok: boolean; status: number; json: () => Promise<unknown> };
    try {
      response = await fetch(buildUrl(page), { signal: options.signal, cache: 'no-store' });
    } catch (error) {
      // Cancellation is intentional — surface it untouched so callers can detect it.
      if ((error as Error)?.name === 'AbortError') throw error;
      throw new StrapiCollectionError(
        `Network error fetching "${normalizedPath}" (page ${page}): ${(error as Error)?.message ?? String(error)}`,
        normalizedPath,
        page,
      );
    }

    if (!response.ok) {
      throw new StrapiCollectionError(
        `Strapi responded ${response.status} for "${normalizedPath}" (page ${page})`,
        normalizedPath,
        page,
        response.status,
      );
    }

    let body: unknown;
    try {
      body = await response.json();
    } catch {
      throw new StrapiCollectionError(
        `Malformed JSON from "${normalizedPath}" (page ${page})`,
        normalizedPath,
        page,
        response.status,
      );
    }

    // Two shapes: standard `{ data, meta }` and the bare-array users-permissions `/users`.
    if (Array.isArray(body)) return { rows: body as T[] };
    const typed = (body ?? {}) as StrapiPage<T>;
    return { rows: typed.data ?? [], pageCount: typed.meta?.pagination?.pageCount };
  };

  const firstPage = await fetchPage(1);

  // Known page count (standard shape): fetch the remaining pages concurrently.
  if (typeof firstPage.pageCount === 'number') {
    if (firstPage.pageCount > maxPages) {
      throw new StrapiCollectionError(
        `"${normalizedPath}" reports ${firstPage.pageCount} pages, exceeding maxPages=${maxPages}`,
        normalizedPath,
      );
    }
    if (firstPage.pageCount <= 1) return firstPage.rows;
    const restPages = await mapWithConcurrency(
      pageRange(2, firstPage.pageCount),
      concurrency,
      fetchPage,
    );
    return firstPage.rows.concat(...restPages.map((p) => p.rows));
  }

  // Unknown page count: a bare-array response with no `meta`. Two sub-cases —
  //  (a) a real paginating endpoint — page sequentially until a short page;
  //  (b) a non-paginating endpoint (users-permissions `/api/users`) that
  //      ignores REST pagination and returns the WHOLE collection on every
  //      request. Its find() -> fetchAll() -> db.query().findMany() path
  //      honors filters and sort but not `pagination`, so the short-page stop
  //      condition below is never met. The two guards detect (b) conclusively.

  // Guard 1 (over-size): a compliant endpoint returns at most `pageSize` rows.
  // More than that proves pagination was ignored — page 1 is the whole set.
  if (firstPage.rows.length > pageSize) return firstPage.rows;

  const allRows: T[] = [...firstPage.rows];
  // A short page already proves we have the whole (possibly empty) collection.
  if (firstPage.rows.length < pageSize) return allRows;

  // Page 1 was exactly full: either the collection size is an exact multiple
  // of `pageSize`, or the endpoint ignores `page` too. Page sequentially; the
  // identical-page guard tells the two apart.
  for (let page = 2; page <= maxPages; page += 1) {
    const next = await fetchPage(page);

    // Guard 1 also applies mid-loop: if a non-paginating endpoint's row count
    // changed between fetches, this page can come back over-size. It is itself
    // the whole (current) collection — page 1 was a stale subset, so drop it.
    if (next.rows.length > pageSize) return next.rows;

    // Guard 2 (identical page): a non-paginating endpoint re-serves the same
    // rows for every `page`. A page identical to page 1 proves page 1 was
    // already the complete collection — stop instead of looping to maxPages.
    if (isSamePage(next.rows, firstPage.rows)) return allRows;

    allRows.push(...next.rows);
    if (next.rows.length < pageSize) return allRows;
  }
  throw new StrapiCollectionError(
    `"${normalizedPath}" exceeded maxPages=${maxPages} without reaching the end`,
    normalizedPath,
  );
}

/** Options for {@link fetchStrapiCollectionByIds}. */
export interface FetchByIdsOptions extends FetchCollectionOptions {
  /** Ids per request. Clamped to a minimum of 1. Default 15. */
  chunkSize?: number;
}

/**
 * Fetch every row matching an `$in` filter over `ids`, splitting the ids across
 * however many requests it takes to keep each query string under the WAF limit.
 *
 * A single `$in` request grows linearly with the id count: 30 news-post
 * documentIds under `commentPopulate` produced a 2244-byte query string, which
 * AWS WAF rejected with a 403 before it reached Strapi. Every comment on a
 * 27-post group then rendered as "0 comments" (MWBI-934). Chunking bounds the
 * query string regardless of how large the collection grows.
 *
 * Each chunk is still paged in full by {@link fetchStrapiCollection}, so row
 * limits and id limits are handled independently. Any chunk failure rejects the
 * whole call — never a silent partial result, matching the guarantee callers
 * already rely on.
 *
 * `concurrency` applies at BOTH levels — chunks here, and pages within each
 * chunk — so a worst case of `concurrency²` requests can be in flight. That
 * only arises when a chunk's rows exceed one 100-row page; the batches this
 * app runs (comments for 15 posts, replies for 15 topics) are single-page, so
 * the practical ceiling is `concurrency`. Lower it if a caller fans out wider.
 *
 * @param path   Collection path, e.g. `news-comments`.
 * @param ids    Ids to filter by. Deduplicated; falsy entries dropped. Empty ⇒ `[]`, no request.
 * @param buildParams Builds the query params for one chunk of ids.
 * @param options chunkSize plus the usual fetchStrapiCollection options.
 * @returns every matching row across all chunks, in chunk order.
 * @throws {StrapiCollectionError} if any chunk fails.
 */
export async function fetchStrapiCollectionByIds<T = unknown>(
  path: string,
  ids: readonly string[],
  buildParams: (idChunk: string[]) => StrapiCollectionParams,
  options: FetchByIdsOptions = {},
): Promise<T[]> {
  const uniqueIds = Array.from(new Set(ids.filter((id): id is string => Boolean(id))));
  if (uniqueIds.length === 0) return [];

  const chunkSize = Math.max(1, Math.trunc(options.chunkSize ?? DEFAULT_ID_CHUNK_SIZE));
  const concurrency = Math.max(1, Math.trunc(options.concurrency ?? DEFAULT_CONCURRENCY));

  const chunks: string[][] = [];
  for (let start = 0; start < uniqueIds.length; start += chunkSize) {
    chunks.push(uniqueIds.slice(start, start + chunkSize));
  }

  const perChunk = await mapWithConcurrency(chunks, concurrency, (chunk) =>
    fetchStrapiCollection<T>(path, buildParams(chunk), options),
  );

  return perChunk.flat();
}
